import { useMutation } from "@tanstack/react-query";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { put } from "api/api-methods";
import { useBookingId } from "hooks/use-booking-id";
import { useError } from "hooks/use-error";
import { error as logError } from "logger/logger";
import queryClient from "query-client";
import { State, Time } from "store/store-types";
import { Contact } from "types/contact";
import {
    removeTime,
    UpdateReservationParameters as UpdateBookingParameters,
} from "types/request-parameters";
import { WebBooking } from "types/web-booking";

export const useUpdateBooking = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const id = useBookingId();
    const { data: booking } = useWebBooking();

    const updateBooking = async ({
        timesToUpdate,
        removeTimes,
        guests,
        messageOverride,
        contact = {} as Contact,
        isPending = true,
        isInquiry = false,
    }: {
        state: State;
        timesToUpdate: Time[];
        removeTimes?: removeTime[];
        guests?: number;
        messageOverride?: string;
        contact?: Contact;
        isPending?: boolean;
        isInquiry?: boolean;
    }) => {
        if (!booking) {
            throw new Error("Booking data is required for updating booking");
        }

        const message = messageOverride ? messageOverride : booking.message;
        const data: UpdateBookingParameters = new UpdateBookingParameters(
            {
                date: booking.start.clone().startOf("day"),
                guests: guests ? guests : booking.guests,
                guestsChildren: booking.guestsChildren,
                contact,
            },
            timesToUpdate,
            id || "",
            removeTimes,
            message,
            isPending,
            isInquiry
        );

        try {
            const booking = await put<WebBooking, UpdateBookingParameters>(
                `WebBooking/WebBookings`,
                data
            );
            await queryClient.invalidateQueries({
                // Todo: We invalidate it because the updated response does not include the webrule. Should fix this on the backend.
                queryKey: ["booking", id],
            });
            return booking;
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            logError(error);
            const errorCode = response?.code;
            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }
            throw error;
        }
    };

    return useMutation({
        mutationFn: updateBooking,
        scope: {
            id: "webbooking-create",
        },
    });
};
